import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { pollRealtime } from './rpc';
import type { RealtimeSignal } from './rpc';

/**
 * **Ce que l'enseignant fait arrive chez l'élève, tout seul, en quelques secondes.**
 *
 * Répétition générale du 7 septembre 2026 : l'enseignant clique « Activer pour cette classe », son
 * écran affiche « Tableur — Activée », et l'écran de l'élève resté ouvert ne bouge pas — soixante
 * secondes, puis indéfiniment. Le cadenas du dock reste posé, et l'icône cadenassée étant
 * désactivée, l'élève ne peut même pas cliquer pour forcer un rafraîchissement : seule sortie, F5,
 * que personne ne lui dit. Devant trente élèves, trente mains se lèvent pour dire que *vous* n'avez
 * pas ouvert l'outil.
 *
 * Cause mesurée dans le code, jamais supposée : `useDeskApps` et `useWorkspace` (`lib/queries.ts`)
 * ont un `staleTime` mais **aucun** `refetchInterval`, et `main.tsx` pose
 * `refetchOnWindowFocus: false` pour tout le bureau. Rien, absolument rien, ne relançait ces deux
 * lectures. Ce fichier est ce qui les relance — sur signal, pas à l'aveugle.
 *
 * ## Le transport : polling, et c'est une décision, pas un pis-aller
 *
 * `GET /api/realtime/poll?since=<rt_seq>` toutes les cinq secondes (dix quand l'onglet est caché).
 * Le plan de livraison (C06) l'a tranché : un proxy de lycée qui bufferise coupe un flux SSE sans
 * rien dire, quand une requête GET courte traverse tout. L'EventSource viendra en V1.1 **derrière
 * ce même fichier** — les écrans ne changeront pas.
 *
 * ## Pourquoi ce hook n'invalide pas tout, tout le temps
 *
 * Un signal ne transporte aucune donnée : il dit « relis ». Chaque type de signal désigne les
 * lectures qu'il périme, et elles seules — recharger les onze requêtes du bureau à chaque clic
 * d'activation ferait clignoter l'écran de l'enseignant pendant qu'il travaille. Mais un type de
 * signal **inconnu de cette table** invalide tout : le jour où le cœur en publiera un quatrième,
 * l'écran le suivra sans qu'on ait à y penser, plutôt que de l'ignorer en silence. Une table
 * incomplète coûte un rafraîchissement de trop ; une table qui laisse tomber coûte une séance.
 *
 * ## Aucune app n'est nommée ici
 *
 * Les signaux sont des faits du cœur (« la liste des outils de cette classe a changé »), jamais
 * « le Tableur a été activé » : le bureau relit `desk.apps` et découvre lui-même ce qui a bougé.
 * C'est la règle 1 de `CLAUDE.md`, et c'est aussi ce qui fait qu'une app installée demain suivra
 * ce fil sans qu'une ligne de ce fichier ne change.
 */

/** Cadence, onglet visible. Valeur du plan de livraison (C06) : « client polling 5 s onglet
 * visible / 10 s onglet caché ». Assez court pour qu'un élève ne se demande jamais s'il doit lever
 * la main, assez espacé pour qu'une classe de trente ne fasse que six requêtes par seconde. */
const POLL_VISIBLE_MS = 5_000;
/** Cadence, onglet caché — l'élève travaille dans un autre onglet, ou l'écran est verrouillé. */
const POLL_HIDDEN_MS = 10_000;

/** Le repère de reprise, propre à l'onglet. `sessionStorage` et non `localStorage` : deux onglets
 * ouverts avancent chacun à leur rythme, et un onglet neuf repart d'un état frais plutôt que
 * d'hériter du repère d'un autre. */
const SINCE_KEY = 'ndrc.rtSince';

/**
 * Ce que chaque signal du cœur périme. Les clés sont celles de `lib/queries.ts` — leur PRÉFIXE
 * suffit : `invalidateQueries` (TanStack Query) apparie par préfixe, donc `['core','desk.apps']`
 * atteint `['core','desk.apps', classId]` pour toutes les classes chargées.
 */
const INVALIDATIONS: Record<string, readonly (readonly string[])[]> = {
  // Le cadenas du dock, et l'écran de l'app ouverte quand elle vient d'être refermée.
  'core.apps.changed': [['core', 'desk.apps']],
  // L'espace de travail que les outils de l'élève ouvrent — c'est lui qui les fait basculer sur le
  // fichier que l'enseignant vient de distribuer (voir `AppHost`, qui remonte l'app quand cet
  // identifiant change) — et, côté enseignant, les deux écrans qui affichent le fichier courant.
  'core.class_file.changed': [
    ['core', 'desk.resolveWorkspace'],
    ['core', 'classes.file'],
    ['core', 'teacher.studentSpace'],
  ],
  // La consigne de la séance, la liste de l'enseignant, et son suivi de qui a terminé.
  'core.activity.changed': [
    ['core', 'activities.current'],
    ['core', 'activities.list'],
    ['core', 'activities.progress'],
  ],
  // La cloche : une notification vient d'être écrite pour un ou plusieurs membres de cette classe
  // (`notifications.send`). Le signal ne dit PAS pour qui — il n'a pas à le dire : chaque onglet
  // relit `notifications.list`, qui ne rend jamais que les siennes. Un élève non destinataire
  // relit une fois et ne trouve rien de neuf.
  'core.notifications.changed': [['core', 'notifications.list']],
  // Un document vient d'être déposé ou retiré : l'écran « Documents » de l'élève, la liste de
  // l'enseignant, et la consigne du jour (qui porte peut-être ce document en pièce jointe) — plus
  // la liste de l'enseignant, où la pièce jointe s'affiche aussi. Les adresses signées y sont
  // refabriquées au passage, ce qui les rafraîchit sans que personne ait à recharger sa page.
  'core.documents.changed': [
    ['core', 'files.list'],
    ['core', 'activities.current'],
    ['core', 'activities.list'],
  ],
  // Publié par le trigger de reconstruction de l'ACL (`0002_rls.sql` §7), sur le canal personnel
  // de chaque utilisateur touché : « les espaces auxquels vous avez accès ont changé ». C'est le
  // seul signal de cette table qui ne vienne pas d'une opération du cœur — il arrive notamment
  // avec une distribution, une fraction de seconde avant `core.class_file.changed`, et vise la
  // même lecture.
  acl_changed: [['core', 'desk.resolveWorkspace']],
};

function applySignals(client: QueryClient, signals: readonly RealtimeSignal[]): void {
  for (const signal of signals) {
    const keys = INVALIDATIONS[signal.type];
    if (keys === undefined) {
      // Type inconnu de cette version du bureau : on relit tout ce que le cœur nous sert plutôt
      // que d'ignorer un signal qu'on ne sait pas lire. Voir le commentaire de tête.
      void client.invalidateQueries({ queryKey: ['core'] });
      continue;
    }
    for (const key of keys) void client.invalidateQueries({ queryKey: key });
  }
}

function readSince(): string | null {
  try {
    const value = sessionStorage.getItem(SINCE_KEY);
    return value === null || value === '' ? null : value;
  } catch {
    // Navigation privée stricte, stockage refusé : le temps réel repart de zéro à chaque
    // rechargement — dégradé, jamais cassé.
    return null;
  }
}

function writeSince(value: string): void {
  try {
    sessionStorage.setItem(SINCE_KEY, value);
  } catch {
    // Voir `readSince`.
  }
}

/**
 * Branche l'onglet sur le fil temps réel. À monter **une fois**, au-dessus du bureau — jamais par
 * écran : deux montages feraient deux requêtes toutes les cinq secondes et se voleraient leur
 * repère de reprise.
 */
export function useRealtimeSignals(): void {
  const client = useQueryClient();

  useEffect(() => {
    let stopped = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const controller = new AbortController();

    const schedule = (): void => {
      if (stopped) return;
      const hidden = document.visibilityState === 'hidden';
      timer = setTimeout(tick, hidden ? POLL_HIDDEN_MS : POLL_VISIBLE_MS);
    };

    const tick = (): void => {
      void (async () => {
        try {
          const result = await pollRealtime(readSince(), controller.signal);
          if (stopped) return;
          writeSince(result.head);
          if (result.resync) {
            // Trop de signaux pour un seul lot (onglet laissé ouvert toute la nuit) : on ne sait
            // plus lesquels comptaient, donc on relit tout. Jamais un lot tronqué, qui ferait
            // manquer en silence précisément celui qui importait.
            void client.invalidateQueries({ queryKey: ['core'] });
          } else {
            applySignals(client, result.signals);
          }
        } catch {
          // Réseau coupé, API redémarrée, session expirée : le fil temps réel est un confort, pas
          // une fonction dont dépend le travail de l'élève. On réessaie au battement suivant — et
          // le repère de reprise n'a pas bougé, donc rien n'est perdu quand le réseau revient.
        } finally {
          schedule();
        }
      })();
    };

    // Immédiatement, puis à intervalle : un onglet qui vient de revenir au premier plan ne doit pas
    // attendre cinq secondes de plus pour rattraper ce qui s'est passé pendant son absence.
    const onVisible = (): void => {
      if (document.visibilityState !== 'visible') return;
      clearTimeout(timer);
      tick();
    };
    document.addEventListener('visibilitychange', onVisible);
    tick();

    return () => {
      stopped = true;
      clearTimeout(timer);
      controller.abort();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [client]);
}
